import { useState, useEffect } from 'react';
import { gameAPI } from '../services/api';
import './Journal.css';

export default function Journal({ gameId, onClose }) {
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quests');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedSettlements, setExpandedSettlements] = useState({});

  useEffect(() => {
    loadJournal();
  }, [gameId]);

  const loadJournal = async () => {
    try {
      const response = await gameAPI.getJournal(gameId);
      setJournal(response.data.journal);
    } catch (err) {
      console.error('Failed to load journal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteTitle.trim() || !noteContent.trim()) return;

    try {
      const response = await gameAPI.addNote(gameId, noteTitle, noteContent);
      setJournal(prev => ({ ...prev, notes: response.data.notes }));
      setNoteTitle('');
      setNoteContent('');
      setShowNoteForm(false);
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  const tabs = [
    { id: 'quests', label: 'Quests', count: journal?.quests?.filter(q => q.status === 'active').length || 0 },
    { id: 'locations', label: 'Places', count: journal?.locations?.length || 0 },
    { id: 'npcs', label: 'People', count: journal?.npcs?.length || 0 },
    { id: 'flora', label: 'Flora', count: journal?.flora?.length || 0 },
    { id: 'notes', label: 'Notes', count: journal?.notes?.length || 0 }
  ];

  const renderContent = () => {
    if (loading) {
      return <div className="journal-loading"><div className="spinner"></div></div>;
    }

    switch (activeTab) {
      case 'quests':
        return (
          <div className="journal-list">
            {journal?.quests?.length === 0 ? (
              <p className="empty-message">No quests recorded yet.</p>
            ) : (
              <>
                <h4>Active Quests</h4>
                {journal?.quests?.filter(q => q.status === 'active').map((quest, idx) => (
                  <div key={idx} className="journal-item quest-item active">
                    <h5>{quest.title}</h5>
                    <p>{quest.description}</p>
                    {quest.giver && <p className="quest-giver">Given by: {quest.giver}</p>}
                    {quest.objectives?.length > 0 && (
                      <ul className="quest-objectives">
                        {quest.objectives.map((obj, i) => (
                          <li key={i} className={obj.completed ? 'completed' : ''}>
                            {obj.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}

                {journal?.quests?.filter(q => q.status !== 'active').length > 0 && (
                  <>
                    <h4 className="mt-2">Completed/Failed</h4>
                    {journal?.quests?.filter(q => q.status !== 'active').map((quest, idx) => (
                      <div key={idx} className={`journal-item quest-item ${quest.status}`}>
                        <h5>
                          {quest.title}
                          <span className={`quest-status ${quest.status}`}>{quest.status}</span>
                        </h5>
                        <p>{quest.description}</p>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        );

      case 'locations':
        // Organize locations hierarchically
        const organizeLocations = () => {
          const locations = journal?.locations || [];
          const hierarchy = {};

          // Group by region
          locations.forEach(loc => {
            const regionName = loc.region || 'Unknown Region';
            const settlementName = loc.settlement || null;
            const locationType = loc.locationType || 'place';

            if (!hierarchy[regionName]) {
              hierarchy[regionName] = {
                info: locationType === 'region' ? loc : null,
                settlements: {},
                places: []
              };
            }

            if (locationType === 'region') {
              hierarchy[regionName].info = loc;
            } else if (locationType === 'settlement') {
              if (!hierarchy[regionName].settlements[loc.name]) {
                hierarchy[regionName].settlements[loc.name] = {
                  info: loc,
                  places: []
                };
              } else {
                hierarchy[regionName].settlements[loc.name].info = loc;
              }
            } else if (locationType === 'place' && settlementName) {
              if (!hierarchy[regionName].settlements[settlementName]) {
                hierarchy[regionName].settlements[settlementName] = {
                  info: null,
                  places: []
                };
              }
              hierarchy[regionName].settlements[settlementName].places.push(loc);
            } else {
              // Place without settlement (wilderness, dungeon, etc.)
              hierarchy[regionName].places.push(loc);
            }
          });

          return hierarchy;
        };

        const toggleRegion = (region) => {
          setExpandedRegions(prev => ({ ...prev, [region]: !prev[region] }));
        };

        const toggleSettlement = (settlement) => {
          setExpandedSettlements(prev => ({ ...prev, [settlement]: !prev[settlement] }));
        };

        const locationHierarchy = organizeLocations();
        const regionNames = Object.keys(locationHierarchy);

        return (
          <div className="journal-list locations-hierarchy">
            {journal?.locations?.length === 0 ? (
              <p className="empty-message">No locations discovered yet.</p>
            ) : (
              regionNames.map((regionName, idx) => {
                const region = locationHierarchy[regionName];
                const isRegionExpanded = expandedRegions[regionName] !== false; // Default to expanded
                const settlementNames = Object.keys(region.settlements);

                return (
                  <div key={idx} className="region-section">
                    {/* Region Header */}
                    <div
                      className="region-header"
                      onClick={() => toggleRegion(regionName)}
                    >
                      <span className={`expand-icon ${isRegionExpanded ? 'expanded' : ''}`}>▶</span>
                      <h4 className="region-name">{regionName}</h4>
                      <span className="region-count">
                        {settlementNames.length + region.places.length} locations
                      </span>
                    </div>

                    {isRegionExpanded && (
                      <div className="region-content">
                        {/* Region description if available */}
                        {region.info?.description && (
                          <p className="region-description">{region.info.description}</p>
                        )}

                        {/* Settlements */}
                        {settlementNames.map((settlementName, sIdx) => {
                          const settlement = region.settlements[settlementName];
                          const isSettlementExpanded = expandedSettlements[settlementName] !== false;

                          return (
                            <div key={sIdx} className="settlement-section">
                              <div
                                className="settlement-header"
                                onClick={() => toggleSettlement(settlementName)}
                              >
                                <span className={`expand-icon ${isSettlementExpanded ? 'expanded' : ''}`}>▶</span>
                                <h5 className="settlement-name">{settlementName}</h5>
                                <span className="settlement-type">
                                  {settlement.info?.type ? capitalize(settlement.info.type) : 'Settlement'}
                                </span>
                              </div>

                              {isSettlementExpanded && (
                                <div className="settlement-content">
                                  {settlement.info?.description && (
                                    <p className="settlement-description">{settlement.info.description}</p>
                                  )}

                                  {/* Places within settlement */}
                                  {settlement.places.length > 0 && (
                                    <div className="places-list">
                                      {settlement.places.map((place, pIdx) => (
                                        <div key={pIdx} className="journal-item location-item place-item">
                                          <div className="item-header">
                                            <h6>{place.name}</h6>
                                            <span className="location-type">{capitalize(place.type)}</span>
                                          </div>
                                          {place.appearance && (
                                            <p className="place-appearance"><em>{place.appearance}</em></p>
                                          )}
                                          {place.description && <p>{place.description}</p>}
                                          {place.npcs?.length > 0 && (
                                            <p className="location-npcs">
                                              <strong>People here:</strong> {place.npcs.join(', ')}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {settlement.places.length === 0 && !settlement.info?.description && (
                                    <p className="empty-message">No specific places discovered yet.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Places directly in region (not in a settlement) */}
                        {region.places.length > 0 && (
                          <div className="region-places">
                            <h5 className="region-places-header">Other Locations</h5>
                            {region.places.map((place, pIdx) => (
                              <div key={pIdx} className="journal-item location-item place-item">
                                <div className="item-header">
                                  <h6>{place.name}</h6>
                                  <span className="location-type">{capitalize(place.type)}</span>
                                </div>
                                {place.appearance && (
                                  <p className="place-appearance"><em>{place.appearance}</em></p>
                                )}
                                {place.description && <p>{place.description}</p>}
                                {place.npcs?.length > 0 && (
                                  <p className="location-npcs">
                                    <strong>People here:</strong> {place.npcs.join(', ')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );

      case 'npcs':
        return (
          <div className="journal-list">
            {journal?.npcs?.length === 0 ? (
              <p className="empty-message">No people encountered yet.</p>
            ) : (
              journal?.npcs?.map((npc, idx) => (
                <div key={idx} className={`journal-item npc-item ${npc.disposition}`}>
                  <div className="item-header">
                    <h5>{npc.name}</h5>
                    <span className={`disposition ${npc.disposition}`}>{capitalize(npc.disposition)}</span>
                  </div>
                  {npc.race && <p className="npc-race">{capitalize(npc.race)}{npc.occupation ? ` - ${npc.occupation}` : ''}</p>}
                  <p>{npc.description}</p>
                  {npc.location && <p className="npc-location">Location: {npc.location}</p>}
                  {!npc.alive && <p className="npc-deceased">Deceased</p>}
                </div>
              ))
            )}
          </div>
        );

      case 'flora':
        return (
          <div className="journal-list">
            {journal?.flora?.length === 0 ? (
              <p className="empty-message">No plants or fungi discovered yet. Explore the wilderness and forage to find them!</p>
            ) : (
              journal?.flora?.map((plant, idx) => (
                <div key={idx} className={`journal-item flora-item ${plant.rarity}`}>
                  <div className="item-header">
                    <h5>{plant.name}</h5>
                    <span className={`flora-type ${plant.type}`}>{capitalize(plant.type)}</span>
                  </div>
                  <span className={`flora-rarity ${plant.rarity}`}>{capitalize(plant.rarity)}</span>
                  {plant.appearance && <p className="flora-appearance"><em>{plant.appearance}</em></p>}
                  <p>{plant.description}</p>
                  {plant.habitat && <p className="flora-habitat"><strong>Found in:</strong> {plant.habitat}</p>}

                  {/* Uses */}
                  {(plant.uses?.medicinal || plant.uses?.culinary || plant.uses?.recreational || plant.uses?.other) && (
                    <div className="flora-uses">
                      <strong>Uses:</strong>
                      <ul>
                        {plant.uses?.medicinal && <li><span className="use-type medicinal">Medicinal:</span> {plant.uses.medicinal}</li>}
                        {plant.uses?.culinary && <li><span className="use-type culinary">Culinary:</span> {plant.uses.culinary}</li>}
                        {plant.uses?.recreational && <li><span className="use-type recreational">Recreational:</span> {plant.uses.recreational}</li>}
                        {plant.uses?.other && <li><span className="use-type other">Other:</span> {plant.uses.other}</li>}
                      </ul>
                    </div>
                  )}

                  {/* Effects */}
                  {(plant.effects?.beneficial?.length > 0 || plant.effects?.harmful?.length > 0) && (
                    <div className="flora-effects">
                      {plant.effects?.beneficial?.length > 0 && (
                        <p className="effects-beneficial"><strong>Benefits:</strong> {plant.effects.beneficial.join(', ')}</p>
                      )}
                      {plant.effects?.harmful?.length > 0 && (
                        <p className="effects-harmful"><strong>Risks:</strong> {plant.effects.harmful.join(', ')}</p>
                      )}
                      {plant.effects?.intoxicating && (
                        <p className="effects-intoxicating"><strong>⚠ Intoxicating</strong></p>
                      )}
                    </div>
                  )}

                  {plant.discoveredAt && (
                    <p className="flora-discovered">Discovered at: {plant.discoveredAt}</p>
                  )}
                </div>
              ))
            )}
          </div>
        );

      case 'notes':
        return (
          <div className="journal-list">
            <button
              className="btn btn-secondary add-note-btn"
              onClick={() => setShowNoteForm(!showNoteForm)}
            >
              {showNoteForm ? 'Cancel' : '+ Add Note'}
            </button>

            {showNoteForm && (
              <form className="note-form" onSubmit={handleAddNote}>
                <input
                  type="text"
                  placeholder="Note title..."
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  required
                />
                <textarea
                  placeholder="Write your note..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={4}
                  required
                />
                <button type="submit" className="btn btn-primary">Save Note</button>
              </form>
            )}

            {journal?.notes?.length === 0 ? (
              <p className="empty-message">No notes yet. Add one to remember important details!</p>
            ) : (
              journal?.notes?.map((note, idx) => (
                <div key={idx} className="journal-item note-item">
                  <h5>{note.title}</h5>
                  <p>{note.content}</p>
                  <p className="note-date">
                    {new Date(note.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="side-panel journal-panel">
      <div className="side-panel-header">
        <h3>Journal</h3>
        <button className="close-btn" onClick={onClose}>&times;</button>
      </div>

      <div className="journal-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`journal-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="side-panel-content">
        {renderContent()}
      </div>
    </div>
  );
}
